import { FieldErrors } from 'common/components/core/FieldErrors'
import React, { useEffect, useRef, useState } from 'react'
import { PublicPageFieldSlugEnum } from 'src/public-pages/enums/public-page-field-slug-enum'
import { useForgotPasswordSubmit } from 'src/public-pages/hooks/use-forgot-password-submit'
import { WebsitePublicLogo } from 'src/public-pages/shared/components/layout/website-public-logo'
import { PublicPageButton } from 'src/public-pages/shared/components/public-page-button'
import { PublicPageField } from 'src/public-pages/shared/components/public-page-field'
import { PublicPageHeading } from 'src/public-pages/shared/components/public-page-heading'
import { PublicPageText } from 'src/public-pages/shared/components/public-page-text'
import {
  Recaptcha,
  RecaptchaHandleInterface,
} from 'src/public-pages/shared/components/recaptha/recaptcha'
import { WebsitePublicPageType } from 'src/shared/enums/website-public-page-form-type'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { CheckCircleIcon } from 'src/shared/icons/check-circle-icon'
import {
  WebsiteButtonsInterface,
  WebsiteFormFieldsSettings,
  WebsiteThemeInterface,
  WebsiteTypographySettingsStylesInterface,
} from 'src/shared/types/website-settings-types'

export interface ForgotPasswordContentProps {
  logoUrl?: string
  isCustomDomain?: boolean
  heading?: WebsiteTypographySettingsStylesInterface
  paragraph?: WebsiteTypographySettingsStylesInterface
  theme?: WebsiteThemeInterface
  formFields?: Partial<WebsiteFormFieldsSettings>
  button?: Partial<WebsiteButtonsInterface>
  linkButton?: Partial<WebsiteButtonsInterface>
}

export const ForgotPasswordContent: React.FC<ForgotPasswordContentProps> = ({
  logoUrl,
  isCustomDomain,
  heading,
  theme,
  formFields,
  paragraph,
  button,
  linkButton: _linkButton,
}) => {
  const { t } = useTypedTranslation()
  const { email, setEmail, handleSubmit, loading, isSuccess, errors } =
    useForgotPasswordSubmit()
  const [captchaToken, setCaptchaToken] = useState('')
  const recaptchaRef = useRef<RecaptchaHandleInterface>(null)

  useEffect(() => {
    if (errors?.root) {
      recaptchaRef.current?.reset()
    }
  }, [errors?.root])

  const redirect = () => {
    window.location.href = `/${WebsitePublicPageType.Login}`
  }

  if (isSuccess) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <CheckCircleIcon style={{ marginBottom: '12px' }} />

        <PublicPageHeading
          heading={heading}
          theme={theme}
          style={{ marginBottom: '12px' }}
        >
          {t('website_public_page.forgot_password.success_title')}
        </PublicPageHeading>

        <PublicPageText
          paragraph={paragraph}
          theme={theme}
          textAlign="center"
          style={{ marginBottom: '40px' }}
        >
          {t('website_public_page.forgot_password.success_text')}
        </PublicPageText>

        <PublicPageButton
          button={button}
          theme={theme}
          text={t('website_public_page.forgot_password.success_button_text')}
          onClick={redirect}
        />
      </div>
    )
  }

  return (
    <div>
      <WebsitePublicLogo
        logoUrl={logoUrl}
        isCustomDomain={isCustomDomain}
        alt="Logo"
      />
      <PublicPageHeading
        heading={heading}
        theme={theme}
        style={{ marginBottom: '40px' }}
      >
        {t('website_public_page.login.forgot_password_button_text')}
      </PublicPageHeading>

      <div>
        <div style={{ marginBottom: '36px' }}>
          <PublicPageText
            paragraph={paragraph}
            theme={theme}
            style={{ marginBottom: '8px' }}
          >
            {t('website_public_page.email.label')}
          </PublicPageText>

          <PublicPageField
            type={PublicPageFieldSlugEnum.Email}
            name={PublicPageFieldSlugEnum.Email}
            formFields={formFields}
            theme={theme}
            value={email}
            onChange={e => setEmail(e.target.value)}
          />
          <FieldErrors errors={errors?.[PublicPageFieldSlugEnum.Email]} />
          <FieldErrors errors={errors?.root} />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <Recaptcha
            ref={recaptchaRef}
            onTokenChange={setCaptchaToken}
            onError={() => setCaptchaToken('')}
          />
        </div>

        <PublicPageButton
          button={button}
          theme={theme}
          text={t('website_public_page.forgot_password.submit_button_text')}
          loading={loading}
          onClick={() => handleSubmit(captchaToken)}
          width="100%"
          paragraph={paragraph}
        />
      </div>
    </div>
  )
}
